import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Response } from '../interfaces/common/response';
import { RestClient, apiVersion } from '../base/rest-client';
import {
  MultiStoreFailureMapGetResponse,
  MultiStoreOperatingConditionGetResponse,
  MultiStorePowerComparisonGetResponse,
  MultiStorePowerTotalGetResponse,
  CurrentEquipmentSettingsGetResponse,
  MultiStoreCustomizeGetResponse,
  MultiStoreRefrigerantGetResponse,
  AreaSitesGetResponse,
} from '../interfaces/multistore-service/multistore';
import { CurrentEquipmentOutputStates } from 'src/app/multi-store-management/interfaces/currentEquipmentOutputStates';
import { ComfortableTemperatureSetting } from 'src/app/multi-store-management/interfaces/comfortableTemperatureSetting';
import { ImportantEquipmentSetting } from 'src/app/multi-store-management/interfaces/importantEquipmentSetting';
import { CustomizeItem } from 'src/app/multi-store-management/interfaces/customize';

const pathOfMultisite = `multisite/${apiVersion}/`;
const pathOfMultistore = `multistore/${apiVersion}/`;

@Injectable()
export class RestClientMultiStoreService extends RestClient {
  /**
   * コンストラクタ
   *
   */
  /**
   * constructor
   *
   */
  constructor() {
    super();
  }

  /////////////////////////////////////////////////////////////////////////////
  //  多店舗管理アプリAPI
  //  multi-store-management-app API
  /////////////////////////////////////////////////////////////////////////////

  /**
   * 故障MAP取得API
   * response body: MultiStoreFailureMapGetResponse
   *
   * @param {string} personId 人ID
   * @param {boolean} polling ポーリング
   * @return {Observable<Response>} status:HTTPステータス
   */
  getMultiStoreFailureMapStatus(
    personId: string,
    polling: boolean,
  ): Observable<Response<MultiStoreFailureMapGetResponse>> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}failureMap/v1/failureMap/${personId}`,
      null,
      null,
      polling,
    );
  }

  /**
   * 機器OnOffと設定温度現在値取得API
   * response body: CurrentEquipmentSettingsGetResponse
   *
   * @param {string} buildingId 物件ID
   * @param {string} equipmentId 機器ID
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Get the current value of the equipment OnOff and set temperature
   * response body: CurrentEquipmentSettingsGetResponse
   * @param {string} buildingId building ID
   * @param {string} equipmentId Equipment ID
   * @return {Observable<Response>} status:HTTP status
   */
  getCurrentEquipmentSettings(
    buildingId: string,
    equipmentId: string,
  ): Observable<Response<CurrentEquipmentSettingsGetResponse>> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}equipmentSettings/v1/buildings/${buildingId}/currentEquipmentSettings/${equipmentId}`,
    );
  }

  /**
   * 機器OnOffと設定温度操作API
   * @param {CurrentEquipmentOutputStates} operations 操作・設定リスト
   * @param {string} equipmentId 機器ID
   * @returns {Observable<Response>} status:HTTPステータス
   */
  /**
   * Equipment OnOff and set temperature operation API
   * @param {CurrentEquipmentOutputStates} operations Operation setting list
   * @param {string} equipmentId Equipment ID
   * @returns {Observable<Response>} status:HTTP status
   */
  postCurrentEquipmentOutputStates(
    operations: CurrentEquipmentOutputStates,
    equipmentId: string,
  ): Observable<Response> {
    return this.restClientCommonService.request(
      'post',
      `${this.endPoint}equipmentStates/v1/currentEquipmentOutputStates/${equipmentId}`,
      operations,
    );
  }

  /**
   * 快適温度設定更新API
   * @param {ComfortableTemperatureSetting} temperatureSetting 設定温度
   * @param {string} zoneId ゾーンID
   * @returns {Observable<Response>} status:HTTPステータス
   */
  /**
   * Comfortable temperature setting update API
   * @param {ComfortableTemperatureSetting} temperatureSetting Setting temperature
   * @param {string} zoneId Zone ID
   * @returns {Observable<Response>} status:HTTP status
   */
  putComfortableTemperatureSetting(
    temperatureSetting: ComfortableTemperatureSetting,
    zoneId: string,
  ): Observable<Response> {
    return this.restClientCommonService.request(
      'put',
      `${this.endPoint}comfortableTemperatureSetting/v1/comfortableTemperatureSetting/${zoneId}`,
      temperatureSetting,
    );
  }

  /**
   * 快適温度一括設定更新API
   * @param {ComfortableTemperatureSetting} temperatureSetting 設定温度
   * @param {string} zoneIdList ゾーンIDリスト
   * @returns {Observable<Response>} status:HTTPステータス
   */
  /**
   * Comfortable temperature bulk setting update API
   * @param {ComfortableTemperatureSetting} temperatureSetting Setting temperature
   * @param {string[]} zoneIdList Zone ID list
   * @returns {Observable<Response>} status:HTTP status
   */
  putComfortableTemperatureBulkSetting(
    temperatureSetting: ComfortableTemperatureSetting,
    zoneIdList: string[],
  ): Observable<Response> {
    return this.restClientCommonService.request(
      'put',
      `${this.endPoint}comfortableTemperatureSettings/v1/comfortableTemperatureSettings`,
      {
        zoneIdList: zoneIdList,
        high: temperatureSetting.high,
        low: temperatureSetting.low,
      },
    );
  }

  /**
   * 運転状況取得API
   * response body: MultiStoreFailureMapGetResponse
   *
   * @param {string} personId 人ID
   * @param {boolean} polling ポーリング
   * @return {Observable<Response>} status:HTTPステータス
   */
  getMultiStoreOperatingCondition(
    personId: string,
    polling: boolean,
  ): Observable<Response<MultiStoreOperatingConditionGetResponse>> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}operatingCondition/v1/operatingCondition/${personId}`,
      null,
      null,
      polling,
    );
  }

  /**
   * 合計電力消費量取得API
   * response body: MultiStorePowerTotalGetResponse
   *
   * @param {string} personId 人ID
   * @param {string} startTime 計算開始時間
   * @param {string} endTime 計算終了時間
   * @param {boolean} polling ポーリング
   * @return {Observable<Response>} status:HTTPステータス
   */
  getMultiStorePowerTotal(
    personId: string,
    startTime: string,
    endTime: string,
    polling: boolean,
  ): Observable<Response<MultiStorePowerTotalGetResponse>> {
    const query = `?startTime=${startTime}&endTime=${endTime}`;
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}powerTotal/v1/powerTotal/${personId}${query}`,
      null,
      null,
      polling,
    );
  }

  /**
   *  消費電力比較結果取得API
   * response body: MultiStoreFailureMapGetResponse
   *
   * @param {string} personId 人ID
   * @return {Observable<Response>} status:HTTPステータス
   */
  getMultiStorePowerComparison(
    personId: string,
  ): Observable<Response<MultiStorePowerComparisonGetResponse>> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}powerComparison/v1/powerComparison/${personId}`,
    );
  }

  /**
   * カスタマイズUI設定取得API
   * response body: MultiStoreCustomizeGetResponse
   *
   * @param {string} companyGroupId カンパニーグループID
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Get the customize UI settings
   * response body: MultiStoreCustomizeGetResponse
   * @param {string} companyGroupId Company group ID
   * @return {Observable<Response>} status:HTTP status
   */
  getMultiStoreCustomize(
    companyGroupId: string,
  ): Observable<Response<MultiStoreCustomizeGetResponse>> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}customize/v1/customize/${companyGroupId}`,
    );
  }

  /**
   * カスタマイズUI設定登録API
   * response body: {}
   *
   * @param {string} companyGroupId 企業グループID
   * @param {items: CustomizeItem[]} customizeItems カスタマイズ設定配列
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Get the customize UI settings
   * response body: {}
   * @param {string} companyGroupId company group ID
   * @param {items: CustomizeItem[]} customizeItems customize setting items
   * @return {Observable<Response>} status:HTTP status
   */
  putMultiStoreCustomize(
    companyGroupId: string,
    customizeItems: { items: CustomizeItem[] },
  ): Observable<Response> {
    return this.restClientCommonService.request(
      'put',
      `${this.endPoint}customize/v1/customize/${companyGroupId}`,
      customizeItems,
    );
  }

  /**
   * 冷媒漏洩情報取得API
   * response body: MultiStoreRefrigerantGetResponse
   *
   * @param {string} personId 人ID
   * @param {boolean} polling ポーリング
   * @return {Observable<Response>} status:HTTPステータス
   */
  getMultiStoreRefrigerant(
    personId: string,
    polling: boolean,
  ): Observable<Response<MultiStoreRefrigerantGetResponse>> {
    // TODO: 一時的な対応のためAPIコールに戻す
    // return new Observable((observer) => {
    //   observer.next(
    //     new Response<MultiStoreRefrigerantGetResponse>(200, {
    //       areaList: responseRefrigerantCare,
    //     }),
    //   );
    // });
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}refrigerantCare/v1/refrigerantCare/${personId}`,
      null,
      null,
      polling,
    );
  }

  /**
   * エリア・サイト取得API
   * response body: AreaSitesGetResponse
   *
   * @param {string} personId 人ID
   * @return {Observable<Response>} status:HTTPステータス
   */
  getAreaSites(personId: string): Observable<Response<AreaSitesGetResponse>> {
    return this.restClientCommonService.request('get', `${this.endPoint}area/v1/areas/${personId}`);
  }

  /**
   * NOTE: ここから下は元となったMaruttoの3-15. 多物件遠隔監視サービス のAPI
   *       コピーしたコンポーネントクラスの参照を消すので手間なので残している
   */

  /**
   * マップキー取得API
   * response body: apiKey(string)
   *
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Map key acquisition API
   * response body: apiKey(string)
   *
   * @return {Observable<Response>} status:HTTP status
   */
  getMultiSiteMapKey(): Observable<Response> {
    return this.restClientCommonService.request('get', `${this.endPoint}${pathOfMultisite}mapkey`);
  }

  /**
   * エッジ状態確認API
   * response body: MultiStoreEdgeStatusGetResponse
   *
   * @param {string} buildingId 物件ID
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Edge status confirmation API
   * response body: MultiStoreEdgeStatusGetResponse
   *
   * @param {string} buildingId Property ID
   * @return {Observable<Response>} status:HTTP status
   */
  getMultiStoreEdgeStatus(buildingId: string): Observable<Response> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}${pathOfMultisite}edgeStates/${buildingId}`,
    );
  }

  /**
   * 機器状態確認API
   * response body: MultiStoreEquipmentStatusGetResponse
   *
   * @param {string} edgeId エッジID
   * @return {Observable<Response>} status:HTTPステータス
   */
  /**
   * Device status confirmation API
   * response body: MultiStoreEquipmentStatusGetResponse
   *
   * @param {string} edgeId Edge ID
   * @return {Observable<Response>} status:HTTP status
   */
  getMultiStoreEquipmentStatus(edgeId: string): Observable<Response> {
    return this.restClientCommonService.request(
      'get',
      `${this.endPoint}${pathOfMultisite}equipmentStates/${edgeId}`,
    );
  }

  /**
   * 24時間監視機器設定API
   * @param {ImportantEquipmentSetting} equipmentSetting 機器設定
   * @returns {Observable<Response>} status:HTTPステータス
   */
  /**
   * Important equipment setting API
   * @param {ImportantEquipmentSetting} equipmentSetting Equipment setting
   * @returns {Observable<Response>} status:HTTP status
   */
  postImportantEquipmentSetting(
    equipmentSetting: ImportantEquipmentSetting,
  ): Observable<Response> {
    return this.restClientCommonService.request(
      'post',
      `${this.endPoint}equipments/v1/equipments/important`,
      equipmentSetting,
    );
  }
}

// TODO: ver3.0.2の暫定対応(固定値レスポンス)のため、新規API作成後は削除する
// This is a temporary solution(Fixed value response) for ver3.0.2, so it will be deleted after creating a new API.

const responseRefrigerantCare = [
  {
    areaId: 'area-id-001',
    areaName: 'area_1',
    buildingList: [
      {
        buildingId: 'c65948ba-3a80-11ef-9e53-c7237fdde60b',
        buildingName: 'Daikin TDPK 003',
        edgeList: [
          {
            edgeId: '5fc2bfa4-3a9e-11ef-96ee-93eb442a0a40',
            edgeName: 'EDGE-TDPK-003',
            edgeStates: {
              errors: true,
              forcedStop: false,
              maintenance: true,
              normal: true,
            },
            zoneList: [
              {
                zoneId: '419285fa-3aa3-11ef-812b-afb1645e4c90',
                zoneName: 'EDGE-TDPK-003',
                equipmentList: [
                  {
                    equipmentId: '498d233c-e9d6-11ef-9911-5bd3d8490eb4',
                    equipmentName: 'AI',
                    onOff: true,
                    currentRefrigerantCareLevel: 74,
                    refrigerantCareLevelList: [
                      {
                        date: '2025-03-01',
                        refrigerantCareLevel: 95,
                      },
                      {
                        date: '2025-03-02',
                        refrigerantCareLevel: 94,
                      },
                      {
                        date: '2025-03-03',
                        refrigerantCareLevel: 93,
                      },
                      {
                        date: '2025-03-04',
                        refrigerantCareLevel: 92,
                      },
                      {
                        date: '2025-03-05',
                        refrigerantCareLevel: 92,
                      },
                      {
                        date: '2025-03-06',
                        refrigerantCareLevel: 90,
                      },
                      {
                        date: '2025-03-07',
                        refrigerantCareLevel: 89,
                      },
                      {
                        date: '2025-03-08',
                        refrigerantCareLevel: 88,
                      },
                      {
                        date: '2025-03-09',
                        refrigerantCareLevel: 87,
                      },
                      {
                        date: '2025-03-10',
                        refrigerantCareLevel: 86,
                      },
                      {
                        date: '2025-03-11',
                        refrigerantCareLevel: 85,
                      },
                      {
                        date: '2025-03-12',
                        refrigerantCareLevel: 84,
                      },
                      {
                        date: '2025-03-13',
                        refrigerantCareLevel: 83,
                      },
                      {
                        date: '2025-03-14',
                        refrigerantCareLevel: 82,
                      },
                      {
                        date: '2025-03-15',
                        refrigerantCareLevel: 81,
                      },
                      {
                        date: '2025-03-16',
                        refrigerantCareLevel: 80,
                      },
                      {
                        date: '2025-03-17',
                        refrigerantCareLevel: 79,
                      },
                      {
                        date: '2025-03-18',
                        refrigerantCareLevel: 78,
                      },
                      {
                        date: '2025-03-19',
                        refrigerantCareLevel: 78,
                      },
                      {
                        date: '2025-03-20',
                        refrigerantCareLevel: 78,
                      },
                      {
                        date: '2025-03-21',
                        refrigerantCareLevel: 77,
                      },
                      {
                        date: '2025-03-22',
                        refrigerantCareLevel: 76,
                      },
                      {
                        date: '2025-03-23',
                        refrigerantCareLevel: 77,
                      },
                      {
                        date: '2025-03-24',
                        refrigerantCareLevel: 76,
                      },
                      {
                        date: '2025-03-25',
                        refrigerantCareLevel: 76,
                      },
                      {
                        date: '2025-03-26',
                        refrigerantCareLevel: 75,
                      },
                      {
                        date: '2025-03-27',
                        refrigerantCareLevel: 75,
                      },
                      {
                        date: '2025-03-28',
                        refrigerantCareLevel: 74,
                      },
                      {
                        date: '2025-03-29',
                        refrigerantCareLevel: 75,
                      },
                      {
                        date: '2025-03-30',
                        refrigerantCareLevel: 74,
                      },
                    ],
                  },
                  {
                    equipmentId: '498d417d-e9d6-11ef-a186-5bd3d8490eb4',
                    equipmentName: 'DO',
                    onOff: true,
                    currentRefrigerantCareLevel: 75,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 93 },
                      { date: '2025-03-02', refrigerantCareLevel: 90 },
                      { date: '2025-03-03', refrigerantCareLevel: 88 },
                      { date: '2025-03-04', refrigerantCareLevel: 87 },
                      { date: '2025-03-05', refrigerantCareLevel: 87 },
                      { date: '2025-03-06', refrigerantCareLevel: 87 },
                      { date: '2025-03-07', refrigerantCareLevel: 86 },
                      { date: '2025-03-08', refrigerantCareLevel: 86 },
                      { date: '2025-03-09', refrigerantCareLevel: 86 },
                      { date: '2025-03-10', refrigerantCareLevel: 85 },
                      { date: '2025-03-11', refrigerantCareLevel: 85 },
                      { date: '2025-03-12', refrigerantCareLevel: 85 },
                      { date: '2025-03-13', refrigerantCareLevel: 85 },
                      { date: '2025-03-14', refrigerantCareLevel: 84 },
                      { date: '2025-03-15', refrigerantCareLevel: 84 },
                      { date: '2025-03-16', refrigerantCareLevel: 84 },
                      { date: '2025-03-17', refrigerantCareLevel: 83 },
                      { date: '2025-03-18', refrigerantCareLevel: 82 },
                      { date: '2025-03-19', refrigerantCareLevel: 81 },
                      { date: '2025-03-20', refrigerantCareLevel: 81 },
                      { date: '2025-03-21', refrigerantCareLevel: 80 },
                      { date: '2025-03-22', refrigerantCareLevel: 80 },
                      { date: '2025-03-23', refrigerantCareLevel: 79 },
                      { date: '2025-03-24', refrigerantCareLevel: 79 },
                      { date: '2025-03-25', refrigerantCareLevel: 79 },
                      { date: '2025-03-26', refrigerantCareLevel: 78 },
                      { date: '2025-03-27', refrigerantCareLevel: 78 },
                      { date: '2025-03-28', refrigerantCareLevel: 78 },
                      { date: '2025-03-29', refrigerantCareLevel: 77 },
                      { date: '2025-03-30', refrigerantCareLevel: 76 },
                    ],
                  },
                  {
                    equipmentId: '63106bdc-8c34-11ef-b2bc-b9a7c6635973',
                    equipmentName: 'PewerMeter-Multisite',
                    onOff: true,
                    currentRefrigerantCareLevel: 76,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 93 },
                      { date: '2025-03-02', refrigerantCareLevel: 92 },
                      { date: '2025-03-03', refrigerantCareLevel: 92 },
                      { date: '2025-03-04', refrigerantCareLevel: 91 },
                      { date: '2025-03-05', refrigerantCareLevel: 89 },
                      { date: '2025-03-06', refrigerantCareLevel: 88 },
                      { date: '2025-03-07', refrigerantCareLevel: 88 },
                      { date: '2025-03-08', refrigerantCareLevel: 88 },
                      { date: '2025-03-09', refrigerantCareLevel: 88 },
                      { date: '2025-03-10', refrigerantCareLevel: 87 },
                      { date: '2025-03-11', refrigerantCareLevel: 87 },
                      { date: '2025-03-12', refrigerantCareLevel: 87 },
                      { date: '2025-03-13', refrigerantCareLevel: 86 },
                      { date: '2025-03-14', refrigerantCareLevel: 85 },
                      { date: '2025-03-15', refrigerantCareLevel: 85 },
                      { date: '2025-03-16', refrigerantCareLevel: 84 },
                      { date: '2025-03-17', refrigerantCareLevel: 84 },
                      { date: '2025-03-18', refrigerantCareLevel: 84 },
                      { date: '2025-03-19', refrigerantCareLevel: 84 },
                      { date: '2025-03-20', refrigerantCareLevel: 84 },
                      { date: '2025-03-21', refrigerantCareLevel: 84 },
                      { date: '2025-03-22', refrigerantCareLevel: 83 },
                      { date: '2025-03-23', refrigerantCareLevel: 83 },
                      { date: '2025-03-24', refrigerantCareLevel: 82 },
                      { date: '2025-03-25', refrigerantCareLevel: 80 },
                      { date: '2025-03-26', refrigerantCareLevel: 79 },
                      { date: '2025-03-27', refrigerantCareLevel: 78 },
                      { date: '2025-03-28', refrigerantCareLevel: 78 },
                      { date: '2025-03-29', refrigerantCareLevel: 77 },
                      { date: '2025-03-30', refrigerantCareLevel: 77 },
                    ],
                  },
                ],
              },
              {
                zoneId: '6eeb48b9-70d5-11ef-a34b-c7b9c9c1d282',
                zoneName: 'Zone3',
                equipmentList: [
                  {
                    equipmentId: '134d9202-8c36-11ef-888f-15e5b7b4e997',
                    equipmentName: '3:35',
                    onOff: false,
                    currentRefrigerantCareLevel: 77,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 93 },
                      { date: '2025-03-02', refrigerantCareLevel: 93 },
                      { date: '2025-03-03', refrigerantCareLevel: 93 },
                      { date: '2025-03-04', refrigerantCareLevel: 91 },
                      { date: '2025-03-05', refrigerantCareLevel: 90 },
                      { date: '2025-03-06', refrigerantCareLevel: 90 },
                      { date: '2025-03-07', refrigerantCareLevel: 90 },
                      { date: '2025-03-08', refrigerantCareLevel: 89 },
                      { date: '2025-03-09', refrigerantCareLevel: 89 },
                      { date: '2025-03-10', refrigerantCareLevel: 87 },
                      { date: '2025-03-11', refrigerantCareLevel: 87 },
                      { date: '2025-03-12', refrigerantCareLevel: 84 },
                      { date: '2025-03-13', refrigerantCareLevel: 84 },
                      { date: '2025-03-14', refrigerantCareLevel: 84 },
                      { date: '2025-03-15', refrigerantCareLevel: 84 },
                      { date: '2025-03-16', refrigerantCareLevel: 83 },
                      { date: '2025-03-17', refrigerantCareLevel: 83 },
                      { date: '2025-03-18', refrigerantCareLevel: 82 },
                      { date: '2025-03-19', refrigerantCareLevel: 82 },
                      { date: '2025-03-20', refrigerantCareLevel: 82 },
                      { date: '2025-03-21', refrigerantCareLevel: 81 },
                      { date: '2025-03-22', refrigerantCareLevel: 81 },
                      { date: '2025-03-23', refrigerantCareLevel: 81 },
                      { date: '2025-03-24', refrigerantCareLevel: 80 },
                      { date: '2025-03-25', refrigerantCareLevel: 79 },
                      { date: '2025-03-26', refrigerantCareLevel: 79 },
                      { date: '2025-03-27', refrigerantCareLevel: 78 },
                      { date: '2025-03-28', refrigerantCareLevel: 77 },
                      { date: '2025-03-29', refrigerantCareLevel: 77 },
                      { date: '2025-03-30', refrigerantCareLevel: 77 },
                    ],
                  },
                  {
                    equipmentId: '134ca2c1-8c36-11ef-b6e9-15e5b7b4e997',
                    equipmentName: '3:36',
                    onOff: true,
                    currentRefrigerantCareLevel: 78,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 93 },
                      { date: '2025-03-02', refrigerantCareLevel: 93 },
                      { date: '2025-03-03', refrigerantCareLevel: 92 },
                      { date: '2025-03-04', refrigerantCareLevel: 91 },
                      { date: '2025-03-05', refrigerantCareLevel: 91 },
                      { date: '2025-03-06', refrigerantCareLevel: 91 },
                      { date: '2025-03-07', refrigerantCareLevel: 90 },
                      { date: '2025-03-08', refrigerantCareLevel: 90 },
                      { date: '2025-03-09', refrigerantCareLevel: 89 },
                      { date: '2025-03-10', refrigerantCareLevel: 89 },
                      { date: '2025-03-11', refrigerantCareLevel: 89 },
                      { date: '2025-03-12', refrigerantCareLevel: 89 },
                      { date: '2025-03-13', refrigerantCareLevel: 88 },
                      { date: '2025-03-14', refrigerantCareLevel: 88 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 87 },
                      { date: '2025-03-17', refrigerantCareLevel: 86 },
                      { date: '2025-03-18', refrigerantCareLevel: 84 },
                      { date: '2025-03-19', refrigerantCareLevel: 84 },
                      { date: '2025-03-20', refrigerantCareLevel: 84 },
                      { date: '2025-03-21', refrigerantCareLevel: 83 },
                      { date: '2025-03-22', refrigerantCareLevel: 83 },
                      { date: '2025-03-23', refrigerantCareLevel: 82 },
                      { date: '2025-03-24', refrigerantCareLevel: 81 },
                      { date: '2025-03-25', refrigerantCareLevel: 81 },
                      { date: '2025-03-26', refrigerantCareLevel: 80 },
                      { date: '2025-03-27', refrigerantCareLevel: 80 },
                      { date: '2025-03-28', refrigerantCareLevel: 80 },
                      { date: '2025-03-29', refrigerantCareLevel: 79 },
                      { date: '2025-03-30', refrigerantCareLevel: 78 },
                    ],
                  },
                  {
                    equipmentId: '134d1535-8c36-11ef-abcb-15e5b7b4e997',
                    equipmentName: '3:37',
                    onOff: true,
                    currentRefrigerantCareLevel: 78,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 94 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 93 },
                      { date: '2025-03-04', refrigerantCareLevel: 93 },
                      { date: '2025-03-05', refrigerantCareLevel: 93 },
                      { date: '2025-03-06', refrigerantCareLevel: 93 },
                      { date: '2025-03-07', refrigerantCareLevel: 92 },
                      { date: '2025-03-08', refrigerantCareLevel: 92 },
                      { date: '2025-03-09', refrigerantCareLevel: 92 },
                      { date: '2025-03-10', refrigerantCareLevel: 91 },
                      { date: '2025-03-11', refrigerantCareLevel: 90 },
                      { date: '2025-03-12', refrigerantCareLevel: 89 },
                      { date: '2025-03-13', refrigerantCareLevel: 89 },
                      { date: '2025-03-14', refrigerantCareLevel: 88 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 85 },
                      { date: '2025-03-17', refrigerantCareLevel: 84 },
                      { date: '2025-03-18', refrigerantCareLevel: 84 },
                      { date: '2025-03-19', refrigerantCareLevel: 83 },
                      { date: '2025-03-20', refrigerantCareLevel: 83 },
                      { date: '2025-03-21', refrigerantCareLevel: 83 },
                      { date: '2025-03-22', refrigerantCareLevel: 82 },
                      { date: '2025-03-23', refrigerantCareLevel: 81 },
                      { date: '2025-03-24', refrigerantCareLevel: 81 },
                      { date: '2025-03-25', refrigerantCareLevel: 80 },
                      { date: '2025-03-26', refrigerantCareLevel: 80 },
                      { date: '2025-03-27', refrigerantCareLevel: 80 },
                      { date: '2025-03-28', refrigerantCareLevel: 79 },
                      { date: '2025-03-29', refrigerantCareLevel: 79 },
                      { date: '2025-03-30', refrigerantCareLevel: 78 },
                    ],
                  },
                  {
                    equipmentId: '134ed9d6-8c36-11ef-8d48-15e5b7b4e997',
                    equipmentName: '3:38',
                    onOff: true,
                    currentRefrigerantCareLevel: 79,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 94 },
                      { date: '2025-03-02', refrigerantCareLevel: 93 },
                      { date: '2025-03-03', refrigerantCareLevel: 93 },
                      { date: '2025-03-04', refrigerantCareLevel: 93 },
                      { date: '2025-03-05', refrigerantCareLevel: 93 },
                      { date: '2025-03-06', refrigerantCareLevel: 92 },
                      { date: '2025-03-07', refrigerantCareLevel: 92 },
                      { date: '2025-03-08', refrigerantCareLevel: 91 },
                      { date: '2025-03-09', refrigerantCareLevel: 90 },
                      { date: '2025-03-10', refrigerantCareLevel: 90 },
                      { date: '2025-03-11', refrigerantCareLevel: 90 },
                      { date: '2025-03-12', refrigerantCareLevel: 90 },
                      { date: '2025-03-13', refrigerantCareLevel: 88 },
                      { date: '2025-03-14', refrigerantCareLevel: 87 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 84 },
                      { date: '2025-03-17', refrigerantCareLevel: 84 },
                      { date: '2025-03-18', refrigerantCareLevel: 83 },
                      { date: '2025-03-19', refrigerantCareLevel: 83 },
                      { date: '2025-03-20', refrigerantCareLevel: 82 },
                      { date: '2025-03-21', refrigerantCareLevel: 82 },
                      { date: '2025-03-22', refrigerantCareLevel: 81 },
                      { date: '2025-03-23', refrigerantCareLevel: 81 },
                      { date: '2025-03-24', refrigerantCareLevel: 81 },
                      { date: '2025-03-25', refrigerantCareLevel: 81 },
                      { date: '2025-03-26', refrigerantCareLevel: 81 },
                      { date: '2025-03-27', refrigerantCareLevel: 81 },
                      { date: '2025-03-28', refrigerantCareLevel: 80 },
                      { date: '2025-03-29', refrigerantCareLevel: 80 },
                      { date: '2025-03-30', refrigerantCareLevel: 79 },
                    ],
                  },
                ],
              },
              {
                zoneId: '42c0a144-3aa3-11ef-8b6a-afb1645e4c90',
                zoneName: 'EDGE-TDPK-003_outdoor',
                equipmentList: [
                  {
                    equipmentId: '134fe795-8c36-11ef-b241-15e5b7b4e997',
                    equipmentName: 'RSRP40BCV_sa_1-15@3',
                    onOff: false,
                    currentRefrigerantCareLevel: 79,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 94 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 93 },
                      { date: '2025-03-04', refrigerantCareLevel: 93 },
                      { date: '2025-03-05', refrigerantCareLevel: 92 },
                      { date: '2025-03-06', refrigerantCareLevel: 91 },
                      { date: '2025-03-07', refrigerantCareLevel: 91 },
                      { date: '2025-03-08', refrigerantCareLevel: 91 },
                      { date: '2025-03-09', refrigerantCareLevel: 90 },
                      { date: '2025-03-10', refrigerantCareLevel: 88 },
                      { date: '2025-03-11', refrigerantCareLevel: 88 },
                      { date: '2025-03-12', refrigerantCareLevel: 88 },
                      { date: '2025-03-13', refrigerantCareLevel: 87 },
                      { date: '2025-03-14', refrigerantCareLevel: 87 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 87 },
                      { date: '2025-03-17', refrigerantCareLevel: 86 },
                      { date: '2025-03-18', refrigerantCareLevel: 86 },
                      { date: '2025-03-19', refrigerantCareLevel: 85 },
                      { date: '2025-03-20', refrigerantCareLevel: 85 },
                      { date: '2025-03-21', refrigerantCareLevel: 83 },
                      { date: '2025-03-22', refrigerantCareLevel: 83 },
                      { date: '2025-03-23', refrigerantCareLevel: 83 },
                      { date: '2025-03-24', refrigerantCareLevel: 82 },
                      { date: '2025-03-25', refrigerantCareLevel: 81 },
                      { date: '2025-03-26', refrigerantCareLevel: 81 },
                      { date: '2025-03-27', refrigerantCareLevel: 81 },
                      { date: '2025-03-28', refrigerantCareLevel: 80 },
                      { date: '2025-03-29', refrigerantCareLevel: 79 },
                      { date: '2025-03-30', refrigerantCareLevel: 79 },
                    ],
                  },
                  {
                    equipmentId: '134f4c4d-8c36-11ef-a975-15e5b7b4e997',
                    equipmentName: 'RSRP40BCV_sa_1-15@1',
                    onOff: true,
                    currentRefrigerantCareLevel: 80,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 94 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 93 },
                      { date: '2025-03-04', refrigerantCareLevel: 92 },
                      { date: '2025-03-05', refrigerantCareLevel: 92 },
                      { date: '2025-03-06', refrigerantCareLevel: 91 },
                      { date: '2025-03-07', refrigerantCareLevel: 91 },
                      { date: '2025-03-08', refrigerantCareLevel: 90 },
                      { date: '2025-03-09', refrigerantCareLevel: 89 },
                      { date: '2025-03-10', refrigerantCareLevel: 88 },
                      { date: '2025-03-11', refrigerantCareLevel: 88 },
                      { date: '2025-03-12', refrigerantCareLevel: 87 },
                      { date: '2025-03-13', refrigerantCareLevel: 87 },
                      { date: '2025-03-14', refrigerantCareLevel: 86 },
                      { date: '2025-03-15', refrigerantCareLevel: 86 },
                      { date: '2025-03-16', refrigerantCareLevel: 86 },
                      { date: '2025-03-17', refrigerantCareLevel: 85 },
                      { date: '2025-03-18', refrigerantCareLevel: 85 },
                      { date: '2025-03-19', refrigerantCareLevel: 85 },
                      { date: '2025-03-20', refrigerantCareLevel: 84 },
                      { date: '2025-03-21', refrigerantCareLevel: 84 },
                      { date: '2025-03-22', refrigerantCareLevel: 84 },
                      { date: '2025-03-23', refrigerantCareLevel: 84 },
                      { date: '2025-03-24', refrigerantCareLevel: 83 },
                      { date: '2025-03-25', refrigerantCareLevel: 82 },
                      { date: '2025-03-26', refrigerantCareLevel: 81 },
                      { date: '2025-03-27', refrigerantCareLevel: 81 },
                      { date: '2025-03-28', refrigerantCareLevel: 81 },
                      { date: '2025-03-29', refrigerantCareLevel: 81 },
                      { date: '2025-03-30', refrigerantCareLevel: 80 },
                    ],
                  },
                  {
                    equipmentId: '1351148f-8c36-11ef-bc14-15e5b7b4e997',
                    equipmentName: 'RSRP40BCV_sa_1-15@2',
                    onOff: true,
                    currentRefrigerantCareLevel: 80,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 95 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 94 },
                      { date: '2025-03-04', refrigerantCareLevel: 94 },
                      { date: '2025-03-05', refrigerantCareLevel: 93 },
                      { date: '2025-03-06', refrigerantCareLevel: 92 },
                      { date: '2025-03-07', refrigerantCareLevel: 91 },
                      { date: '2025-03-08', refrigerantCareLevel: 91 },
                      { date: '2025-03-09', refrigerantCareLevel: 90 },
                      { date: '2025-03-10', refrigerantCareLevel: 90 },
                      { date: '2025-03-11', refrigerantCareLevel: 90 },
                      { date: '2025-03-12', refrigerantCareLevel: 88 },
                      { date: '2025-03-13', refrigerantCareLevel: 87 },
                      { date: '2025-03-14', refrigerantCareLevel: 87 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 86 },
                      { date: '2025-03-17', refrigerantCareLevel: 86 },
                      { date: '2025-03-18', refrigerantCareLevel: 86 },
                      { date: '2025-03-19', refrigerantCareLevel: 86 },
                      { date: '2025-03-20', refrigerantCareLevel: 86 },
                      { date: '2025-03-21', refrigerantCareLevel: 86 },
                      { date: '2025-03-22', refrigerantCareLevel: 86 },
                      { date: '2025-03-23', refrigerantCareLevel: 84 },
                      { date: '2025-03-24', refrigerantCareLevel: 83 },
                      { date: '2025-03-25', refrigerantCareLevel: 83 },
                      { date: '2025-03-26', refrigerantCareLevel: 82 },
                      { date: '2025-03-27', refrigerantCareLevel: 81 },
                      { date: '2025-03-28', refrigerantCareLevel: 81 },
                      { date: '2025-03-29', refrigerantCareLevel: 81 },
                      { date: '2025-03-30', refrigerantCareLevel: 81 },
                    ],
                  },
                ],
              },
              {
                zoneId: '683dce3f-70d5-11ef-9713-c7b9c9c1d282',
                zoneName: 'Zone2',
                equipmentList: [
                  {
                    equipmentId: '134e6e07-8c36-11ef-b54c-15e5b7b4e997',
                    equipmentName: '2:18',
                    onOff: false,
                    currentRefrigerantCareLevel: 81,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 95 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 94 },
                      { date: '2025-03-04', refrigerantCareLevel: 94 },
                      { date: '2025-03-05', refrigerantCareLevel: 94 },
                      { date: '2025-03-06', refrigerantCareLevel: 93 },
                      { date: '2025-03-07', refrigerantCareLevel: 93 },
                      { date: '2025-03-08', refrigerantCareLevel: 93 },
                      { date: '2025-03-09', refrigerantCareLevel: 93 },
                      { date: '2025-03-10', refrigerantCareLevel: 92 },
                      { date: '2025-03-11', refrigerantCareLevel: 91 },
                      { date: '2025-03-12', refrigerantCareLevel: 90 },
                      { date: '2025-03-13', refrigerantCareLevel: 89 },
                      { date: '2025-03-14', refrigerantCareLevel: 89 },
                      { date: '2025-03-15', refrigerantCareLevel: 89 },
                      { date: '2025-03-16', refrigerantCareLevel: 88 },
                      { date: '2025-03-17', refrigerantCareLevel: 88 },
                      { date: '2025-03-18', refrigerantCareLevel: 87 },
                      { date: '2025-03-19', refrigerantCareLevel: 87 },
                      { date: '2025-03-20', refrigerantCareLevel: 87 },
                      { date: '2025-03-21', refrigerantCareLevel: 86 },
                      { date: '2025-03-22', refrigerantCareLevel: 86 },
                      { date: '2025-03-23', refrigerantCareLevel: 85 },
                      { date: '2025-03-24', refrigerantCareLevel: 85 },
                      { date: '2025-03-25', refrigerantCareLevel: 85 },
                      { date: '2025-03-26', refrigerantCareLevel: 83 },
                      { date: '2025-03-27', refrigerantCareLevel: 81 },
                      { date: '2025-03-28', refrigerantCareLevel: 81 },
                      { date: '2025-03-29', refrigerantCareLevel: 81 },
                      { date: '2025-03-30', refrigerantCareLevel: 81 },
                    ],
                  },
                  {
                    equipmentId: '13523c90-8c36-11ef-8fa4-15e5b7b4e997',
                    equipmentName: '2:19',
                    onOff: true,
                    currentRefrigerantCareLevel: 81,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 96 },
                      { date: '2025-03-02', refrigerantCareLevel: 94 },
                      { date: '2025-03-03', refrigerantCareLevel: 94 },
                      { date: '2025-03-04', refrigerantCareLevel: 94 },
                      { date: '2025-03-05', refrigerantCareLevel: 94 },
                      { date: '2025-03-06', refrigerantCareLevel: 94 },
                      { date: '2025-03-07', refrigerantCareLevel: 93 },
                      { date: '2025-03-08', refrigerantCareLevel: 90 },
                      { date: '2025-03-09', refrigerantCareLevel: 89 },
                      { date: '2025-03-10', refrigerantCareLevel: 89 },
                      { date: '2025-03-11', refrigerantCareLevel: 89 },
                      { date: '2025-03-12', refrigerantCareLevel: 89 },
                      { date: '2025-03-13', refrigerantCareLevel: 88 },
                      { date: '2025-03-14', refrigerantCareLevel: 87 },
                      { date: '2025-03-15', refrigerantCareLevel: 87 },
                      { date: '2025-03-16', refrigerantCareLevel: 87 },
                      { date: '2025-03-17', refrigerantCareLevel: 86 },
                      { date: '2025-03-18', refrigerantCareLevel: 86 },
                      { date: '2025-03-19', refrigerantCareLevel: 85 },
                      { date: '2025-03-20', refrigerantCareLevel: 85 },
                      { date: '2025-03-21', refrigerantCareLevel: 84 },
                      { date: '2025-03-22', refrigerantCareLevel: 84 },
                      { date: '2025-03-23', refrigerantCareLevel: 83 },
                      { date: '2025-03-24', refrigerantCareLevel: 83 },
                      { date: '2025-03-25', refrigerantCareLevel: 82 },
                      { date: '2025-03-26', refrigerantCareLevel: 82 },
                      { date: '2025-03-27', refrigerantCareLevel: 82 },
                      { date: '2025-03-28', refrigerantCareLevel: 82 },
                      { date: '2025-03-29', refrigerantCareLevel: 81 },
                      { date: '2025-03-30', refrigerantCareLevel: 81 },
                    ],
                  },
                  {
                    equipmentId: '1350842a-8c36-11ef-a94a-15e5b7b4e997',
                    equipmentName: '2:20',
                    onOff: true,
                    currentRefrigerantCareLevel: 82,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 97 },
                      { date: '2025-03-02', refrigerantCareLevel: 95 },
                      { date: '2025-03-03', refrigerantCareLevel: 95 },
                      { date: '2025-03-04', refrigerantCareLevel: 95 },
                      { date: '2025-03-05', refrigerantCareLevel: 95 },
                      { date: '2025-03-06', refrigerantCareLevel: 94 },
                      { date: '2025-03-07', refrigerantCareLevel: 94 },
                      { date: '2025-03-08', refrigerantCareLevel: 93 },
                      { date: '2025-03-09', refrigerantCareLevel: 93 },
                      { date: '2025-03-10', refrigerantCareLevel: 92 },
                      { date: '2025-03-11', refrigerantCareLevel: 91 },
                      { date: '2025-03-12', refrigerantCareLevel: 91 },
                      { date: '2025-03-13', refrigerantCareLevel: 91 },
                      { date: '2025-03-14', refrigerantCareLevel: 91 },
                      { date: '2025-03-15', refrigerantCareLevel: 90 },
                      { date: '2025-03-16', refrigerantCareLevel: 89 },
                      { date: '2025-03-17', refrigerantCareLevel: 87 },
                      { date: '2025-03-18', refrigerantCareLevel: 86 },
                      { date: '2025-03-19', refrigerantCareLevel: 86 },
                      { date: '2025-03-20', refrigerantCareLevel: 86 },
                      { date: '2025-03-21', refrigerantCareLevel: 85 },
                      { date: '2025-03-22', refrigerantCareLevel: 84 },
                      { date: '2025-03-23', refrigerantCareLevel: 84 },
                      { date: '2025-03-24', refrigerantCareLevel: 84 },
                      { date: '2025-03-25', refrigerantCareLevel: 83 },
                      { date: '2025-03-26', refrigerantCareLevel: 83 },
                      { date: '2025-03-27', refrigerantCareLevel: 83 },
                      { date: '2025-03-28', refrigerantCareLevel: 82 },
                      { date: '2025-03-29', refrigerantCareLevel: 82 },
                      { date: '2025-03-30', refrigerantCareLevel: 82 },
                    ],
                  },
                  {
                    equipmentId: '1353bd43-8c36-11ef-83ad-15e5b7b4e997',
                    equipmentName: '2:21',
                    onOff: true,
                    currentRefrigerantCareLevel: 82,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 98 },
                      { date: '2025-03-02', refrigerantCareLevel: 98 },
                      { date: '2025-03-03', refrigerantCareLevel: 97 },
                      { date: '2025-03-04', refrigerantCareLevel: 97 },
                      { date: '2025-03-05', refrigerantCareLevel: 95 },
                      { date: '2025-03-06', refrigerantCareLevel: 95 },
                      { date: '2025-03-07', refrigerantCareLevel: 94 },
                      { date: '2025-03-08', refrigerantCareLevel: 92 },
                      { date: '2025-03-09', refrigerantCareLevel: 92 },
                      { date: '2025-03-10', refrigerantCareLevel: 92 },
                      { date: '2025-03-11', refrigerantCareLevel: 92 },
                      { date: '2025-03-12', refrigerantCareLevel: 91 },
                      { date: '2025-03-13', refrigerantCareLevel: 91 },
                      { date: '2025-03-14', refrigerantCareLevel: 90 },
                      { date: '2025-03-15', refrigerantCareLevel: 89 },
                      { date: '2025-03-16', refrigerantCareLevel: 89 },
                      { date: '2025-03-17', refrigerantCareLevel: 89 },
                      { date: '2025-03-18', refrigerantCareLevel: 88 },
                      { date: '2025-03-19', refrigerantCareLevel: 87 },
                      { date: '2025-03-20', refrigerantCareLevel: 87 },
                      { date: '2025-03-21', refrigerantCareLevel: 87 },
                      { date: '2025-03-22', refrigerantCareLevel: 86 },
                      { date: '2025-03-23', refrigerantCareLevel: 86 },
                      { date: '2025-03-24', refrigerantCareLevel: 85 },
                      { date: '2025-03-25', refrigerantCareLevel: 84 },
                      { date: '2025-03-26', refrigerantCareLevel: 83 },
                      { date: '2025-03-27', refrigerantCareLevel: 83 },
                      { date: '2025-03-28', refrigerantCareLevel: 83 },
                      { date: '2025-03-29', refrigerantCareLevel: 83 },
                      { date: '2025-03-30', refrigerantCareLevel: 82 },
                    ],
                  },
                  {
                    equipmentId: '134dff9a-8c36-11ef-8330-15e5b7b4e997',
                    equipmentName: '2:22',
                    onOff: true,
                    currentRefrigerantCareLevel: 83,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 98 },
                      { date: '2025-03-02', refrigerantCareLevel: 97 },
                      { date: '2025-03-03', refrigerantCareLevel: 97 },
                      { date: '2025-03-04', refrigerantCareLevel: 96 },
                      { date: '2025-03-05', refrigerantCareLevel: 96 },
                      { date: '2025-03-06', refrigerantCareLevel: 96 },
                      { date: '2025-03-07', refrigerantCareLevel: 96 },
                      { date: '2025-03-08', refrigerantCareLevel: 95 },
                      { date: '2025-03-09', refrigerantCareLevel: 94 },
                      { date: '2025-03-10', refrigerantCareLevel: 94 },
                      { date: '2025-03-11', refrigerantCareLevel: 94 },
                      { date: '2025-03-12', refrigerantCareLevel: 93 },
                      { date: '2025-03-13', refrigerantCareLevel: 92 },
                      { date: '2025-03-14', refrigerantCareLevel: 92 },
                      { date: '2025-03-15', refrigerantCareLevel: 91 },
                      { date: '2025-03-16', refrigerantCareLevel: 90 },
                      { date: '2025-03-17', refrigerantCareLevel: 89 },
                      { date: '2025-03-18', refrigerantCareLevel: 88 },
                      { date: '2025-03-19', refrigerantCareLevel: 86 },
                      { date: '2025-03-20', refrigerantCareLevel: 86 },
                      { date: '2025-03-21', refrigerantCareLevel: 85 },
                      { date: '2025-03-22', refrigerantCareLevel: 85 },
                      { date: '2025-03-23', refrigerantCareLevel: 84 },
                      { date: '2025-03-24', refrigerantCareLevel: 84 },
                      { date: '2025-03-25', refrigerantCareLevel: 84 },
                      { date: '2025-03-26', refrigerantCareLevel: 84 },
                      { date: '2025-03-27', refrigerantCareLevel: 83 },
                      { date: '2025-03-28', refrigerantCareLevel: 83 },
                      { date: '2025-03-29', refrigerantCareLevel: 83 },
                      { date: '2025-03-30', refrigerantCareLevel: 83 },
                    ],
                  },
                ],
              },
              {
                zoneId: '2e220cd7-70d5-11ef-a4cc-c7b9c9c1d282',
                zoneName: 'Zone1',
                equipmentList: [
                  {
                    equipmentId: '1353391a-8c36-11ef-9f9b-15e5b7b4e997',
                    equipmentName: '1:0',
                    onOff: false,
                    currentRefrigerantCareLevel: 83,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 99 },
                      { date: '2025-03-02', refrigerantCareLevel: 98 },
                      { date: '2025-03-03', refrigerantCareLevel: 97 },
                      { date: '2025-03-04', refrigerantCareLevel: 96 },
                      { date: '2025-03-05', refrigerantCareLevel: 96 },
                      { date: '2025-03-06', refrigerantCareLevel: 96 },
                      { date: '2025-03-07', refrigerantCareLevel: 95 },
                      { date: '2025-03-08', refrigerantCareLevel: 95 },
                      { date: '2025-03-09', refrigerantCareLevel: 94 },
                      { date: '2025-03-10', refrigerantCareLevel: 94 },
                      { date: '2025-03-11', refrigerantCareLevel: 93 },
                      { date: '2025-03-12', refrigerantCareLevel: 93 },
                      { date: '2025-03-13', refrigerantCareLevel: 92 },
                      { date: '2025-03-14', refrigerantCareLevel: 92 },
                      { date: '2025-03-15', refrigerantCareLevel: 91 },
                      { date: '2025-03-16', refrigerantCareLevel: 91 },
                      { date: '2025-03-17', refrigerantCareLevel: 89 },
                      { date: '2025-03-18', refrigerantCareLevel: 89 },
                      { date: '2025-03-19', refrigerantCareLevel: 88 },
                      { date: '2025-03-20', refrigerantCareLevel: 87 },
                      { date: '2025-03-21', refrigerantCareLevel: 86 },
                      { date: '2025-03-22', refrigerantCareLevel: 86 },
                      { date: '2025-03-23', refrigerantCareLevel: 85 },
                      { date: '2025-03-24', refrigerantCareLevel: 85 },
                      { date: '2025-03-25', refrigerantCareLevel: 85 },
                      { date: '2025-03-26', refrigerantCareLevel: 85 },
                      { date: '2025-03-27', refrigerantCareLevel: 84 },
                      { date: '2025-03-28', refrigerantCareLevel: 84 },
                      { date: '2025-03-29', refrigerantCareLevel: 83 },
                      { date: '2025-03-30', refrigerantCareLevel: 83 },
                    ],
                  },
                  {
                    equipmentId: '1351b53f-8c36-11ef-ac94-15e5b7b4e997',
                    equipmentName: '1:1',
                    onOff: true,
                    currentRefrigerantCareLevel: 84,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 99 },
                      { date: '2025-03-02', refrigerantCareLevel: 99 },
                      { date: '2025-03-03', refrigerantCareLevel: 98 },
                      { date: '2025-03-04', refrigerantCareLevel: 97 },
                      { date: '2025-03-05', refrigerantCareLevel: 96 },
                      { date: '2025-03-06', refrigerantCareLevel: 96 },
                      { date: '2025-03-07', refrigerantCareLevel: 96 },
                      { date: '2025-03-08', refrigerantCareLevel: 96 },
                      { date: '2025-03-09', refrigerantCareLevel: 96 },
                      { date: '2025-03-10', refrigerantCareLevel: 96 },
                      { date: '2025-03-11', refrigerantCareLevel: 96 },
                      { date: '2025-03-12', refrigerantCareLevel: 95 },
                      { date: '2025-03-13', refrigerantCareLevel: 94 },
                      { date: '2025-03-14', refrigerantCareLevel: 92 },
                      { date: '2025-03-15', refrigerantCareLevel: 92 },
                      { date: '2025-03-16', refrigerantCareLevel: 91 },
                      { date: '2025-03-17', refrigerantCareLevel: 91 },
                      { date: '2025-03-18', refrigerantCareLevel: 89 },
                      { date: '2025-03-19', refrigerantCareLevel: 88 },
                      { date: '2025-03-20', refrigerantCareLevel: 88 },
                      { date: '2025-03-21', refrigerantCareLevel: 88 },
                      { date: '2025-03-22', refrigerantCareLevel: 87 },
                      { date: '2025-03-23', refrigerantCareLevel: 87 },
                      { date: '2025-03-24', refrigerantCareLevel: 87 },
                      { date: '2025-03-25', refrigerantCareLevel: 87 },
                      { date: '2025-03-26', refrigerantCareLevel: 86 },
                      { date: '2025-03-27', refrigerantCareLevel: 85 },
                      { date: '2025-03-28', refrigerantCareLevel: 85 },
                      { date: '2025-03-29', refrigerantCareLevel: 85 },
                      { date: '2025-03-30', refrigerantCareLevel: 85 },
                    ],
                  },
                  {
                    equipmentId: '134c21cc-8c36-11ef-add0-15e5b7b4e997',
                    equipmentName: '1:2',
                    onOff: true,
                    currentRefrigerantCareLevel: 85,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 100 },
                      { date: '2025-03-02', refrigerantCareLevel: 100 },
                      { date: '2025-03-03', refrigerantCareLevel: 99 },
                      { date: '2025-03-04', refrigerantCareLevel: 99 },
                      { date: '2025-03-05', refrigerantCareLevel: 99 },
                      { date: '2025-03-06', refrigerantCareLevel: 98 },
                      { date: '2025-03-07', refrigerantCareLevel: 98 },
                      { date: '2025-03-08', refrigerantCareLevel: 98 },
                      { date: '2025-03-09', refrigerantCareLevel: 97 },
                      { date: '2025-03-10', refrigerantCareLevel: 97 },
                      { date: '2025-03-11', refrigerantCareLevel: 97 },
                      { date: '2025-03-12', refrigerantCareLevel: 97 },
                      { date: '2025-03-13', refrigerantCareLevel: 97 },
                      { date: '2025-03-14', refrigerantCareLevel: 96 },
                      { date: '2025-03-15', refrigerantCareLevel: 96 },
                      { date: '2025-03-16', refrigerantCareLevel: 95 },
                      { date: '2025-03-17', refrigerantCareLevel: 95 },
                      { date: '2025-03-18', refrigerantCareLevel: 94 },
                      { date: '2025-03-19', refrigerantCareLevel: 93 },
                      { date: '2025-03-20', refrigerantCareLevel: 93 },
                      { date: '2025-03-21', refrigerantCareLevel: 92 },
                      { date: '2025-03-22', refrigerantCareLevel: 91 },
                      { date: '2025-03-23', refrigerantCareLevel: 91 },
                      { date: '2025-03-24', refrigerantCareLevel: 91 },
                      { date: '2025-03-25', refrigerantCareLevel: 89 },
                      { date: '2025-03-26', refrigerantCareLevel: 88 },
                      { date: '2025-03-27', refrigerantCareLevel: 87 },
                      { date: '2025-03-28', refrigerantCareLevel: 86 },
                      { date: '2025-03-29', refrigerantCareLevel: 86 },
                      { date: '2025-03-30', refrigerantCareLevel: 85 },
                    ],
                  },
                  {
                    equipmentId: '1352bb59-8c36-11ef-b6fc-15e5b7b4e997',
                    equipmentName: '1:3',
                    onOff: true,
                    currentRefrigerantCareLevel: 85,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 100 },
                      { date: '2025-03-02', refrigerantCareLevel: 98 },
                      { date: '2025-03-03', refrigerantCareLevel: 98 },
                      { date: '2025-03-04', refrigerantCareLevel: 98 },
                      { date: '2025-03-05', refrigerantCareLevel: 97 },
                      { date: '2025-03-06', refrigerantCareLevel: 96 },
                      { date: '2025-03-07', refrigerantCareLevel: 96 },
                      { date: '2025-03-08', refrigerantCareLevel: 96 },
                      { date: '2025-03-09', refrigerantCareLevel: 95 },
                      { date: '2025-03-10', refrigerantCareLevel: 93 },
                      { date: '2025-03-11', refrigerantCareLevel: 92 },
                      { date: '2025-03-12', refrigerantCareLevel: 92 },
                      { date: '2025-03-13', refrigerantCareLevel: 92 },
                      { date: '2025-03-14', refrigerantCareLevel: 92 },
                      { date: '2025-03-15', refrigerantCareLevel: 90 },
                      { date: '2025-03-16', refrigerantCareLevel: 90 },
                      { date: '2025-03-17', refrigerantCareLevel: 89 },
                      { date: '2025-03-18', refrigerantCareLevel: 89 },
                      { date: '2025-03-19', refrigerantCareLevel: 89 },
                      { date: '2025-03-20', refrigerantCareLevel: 89 },
                      { date: '2025-03-21', refrigerantCareLevel: 89 },
                      { date: '2025-03-22', refrigerantCareLevel: 88 },
                      { date: '2025-03-23', refrigerantCareLevel: 87 },
                      { date: '2025-03-24', refrigerantCareLevel: 87 },
                      { date: '2025-03-25', refrigerantCareLevel: 86 },
                      { date: '2025-03-26', refrigerantCareLevel: 86 },
                      { date: '2025-03-27', refrigerantCareLevel: 86 },
                      { date: '2025-03-28', refrigerantCareLevel: 86 },
                      { date: '2025-03-29', refrigerantCareLevel: 86 },
                      { date: '2025-03-30', refrigerantCareLevel: 85 },
                    ],
                  },
                  {
                    equipmentId: '13543a26-8c36-11ef-8a28-15e5b7b4e997',
                    equipmentName: '1:4',
                    onOff: true,
                    currentRefrigerantCareLevel: 86,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 101 },
                      { date: '2025-03-02', refrigerantCareLevel: 99 },
                      { date: '2025-03-03', refrigerantCareLevel: 99 },
                      { date: '2025-03-04', refrigerantCareLevel: 98 },
                      { date: '2025-03-05', refrigerantCareLevel: 98 },
                      { date: '2025-03-06', refrigerantCareLevel: 98 },
                      { date: '2025-03-07', refrigerantCareLevel: 98 },
                      { date: '2025-03-08', refrigerantCareLevel: 98 },
                      { date: '2025-03-09', refrigerantCareLevel: 97 },
                      { date: '2025-03-10', refrigerantCareLevel: 97 },
                      { date: '2025-03-11', refrigerantCareLevel: 97 },
                      { date: '2025-03-12', refrigerantCareLevel: 96 },
                      { date: '2025-03-13', refrigerantCareLevel: 96 },
                      { date: '2025-03-14', refrigerantCareLevel: 95 },
                      { date: '2025-03-15', refrigerantCareLevel: 95 },
                      { date: '2025-03-16', refrigerantCareLevel: 94 },
                      { date: '2025-03-17', refrigerantCareLevel: 94 },
                      { date: '2025-03-18', refrigerantCareLevel: 93 },
                      { date: '2025-03-19', refrigerantCareLevel: 93 },
                      { date: '2025-03-20', refrigerantCareLevel: 93 },
                      { date: '2025-03-21', refrigerantCareLevel: 92 },
                      { date: '2025-03-22', refrigerantCareLevel: 91 },
                      { date: '2025-03-23', refrigerantCareLevel: 91 },
                      { date: '2025-03-24', refrigerantCareLevel: 91 },
                      { date: '2025-03-25', refrigerantCareLevel: 90 },
                      { date: '2025-03-26', refrigerantCareLevel: 90 },
                      { date: '2025-03-27', refrigerantCareLevel: 90 },
                      { date: '2025-03-28', refrigerantCareLevel: 90 },
                      { date: '2025-03-29', refrigerantCareLevel: 88 },
                      { date: '2025-03-30', refrigerantCareLevel: 86 },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    areaId: 'area-id-002',
    areaName: 'area_2',
    buildingList: [
      {
        buildingId: 'c65d9dbc-3a80-11ef-8e66-c7237fdde60b',
        buildingName: 'Daikin TDPK 002',
        buildingStates: {
          errors: true,
          forcedStop: false,
          maintenance: false,
          normal: false,
        },
        edgeList: [
          {
            edgeId: 'bf8934a7-3aa5-11ef-9eea-bfb6327e9872',
            edgeName: 'EDGE-TDPK-002',
            edgeStates: {
              errors: true,
              forcedStop: false,
              maintenance: false,
              normal: true,
            },
            zoneList: [
              {
                zoneId: 'e23072eb-74ee-11ef-9549-cf69102e343e',
                zoneName: 'TDPK_Zone2',
                equipmentList: [
                  {
                    equipmentId: '35a6776f-546f-11ef-a152-3d5adfbd80a0',
                    equipmentName: 'FXYFP28DA_1-01@4',
                    onOff: true,
                    currentRefrigerantCareLevel: 88,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 101 },
                      { date: '2025-03-02', refrigerantCareLevel: 100 },
                      { date: '2025-03-03', refrigerantCareLevel: 100 },
                      { date: '2025-03-04', refrigerantCareLevel: 100 },
                      { date: '2025-03-05', refrigerantCareLevel: 99 },
                      { date: '2025-03-06', refrigerantCareLevel: 98 },
                      { date: '2025-03-07', refrigerantCareLevel: 98 },
                      { date: '2025-03-08', refrigerantCareLevel: 97 },
                      { date: '2025-03-09', refrigerantCareLevel: 97 },
                      { date: '2025-03-10', refrigerantCareLevel: 97 },
                      { date: '2025-03-11', refrigerantCareLevel: 97 },
                      { date: '2025-03-12', refrigerantCareLevel: 97 },
                      { date: '2025-03-13', refrigerantCareLevel: 96 },
                      { date: '2025-03-14', refrigerantCareLevel: 96 },
                      { date: '2025-03-15', refrigerantCareLevel: 96 },
                      { date: '2025-03-16', refrigerantCareLevel: 94 },
                      { date: '2025-03-17', refrigerantCareLevel: 93 },
                      { date: '2025-03-18', refrigerantCareLevel: 93 },
                      { date: '2025-03-19', refrigerantCareLevel: 93 },
                      { date: '2025-03-20', refrigerantCareLevel: 93 },
                      { date: '2025-03-21', refrigerantCareLevel: 92 },
                      { date: '2025-03-22', refrigerantCareLevel: 92 },
                      { date: '2025-03-23', refrigerantCareLevel: 91 },
                      { date: '2025-03-24', refrigerantCareLevel: 90 },
                      { date: '2025-03-25', refrigerantCareLevel: 90 },
                      { date: '2025-03-26', refrigerantCareLevel: 90 },
                      { date: '2025-03-27', refrigerantCareLevel: 89 },
                      { date: '2025-03-28', refrigerantCareLevel: 89 },
                      { date: '2025-03-29', refrigerantCareLevel: 88 },
                      { date: '2025-03-30', refrigerantCareLevel: 88 },
                    ],
                  },
                  {
                    equipmentId: '35a73b02-546f-11ef-8957-3d5adfbd80a0',
                    equipmentName: 'FXYFP28DA_1-01@5',
                    onOff: true,
                    currentRefrigerantCareLevel: 89,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 102 },
                      { date: '2025-03-02', refrigerantCareLevel: 102 },
                      { date: '2025-03-03', refrigerantCareLevel: 101 },
                      { date: '2025-03-04', refrigerantCareLevel: 100 },
                      { date: '2025-03-05', refrigerantCareLevel: 100 },
                      { date: '2025-03-06', refrigerantCareLevel: 100 },
                      { date: '2025-03-07', refrigerantCareLevel: 99 },
                      { date: '2025-03-08', refrigerantCareLevel: 99 },
                      { date: '2025-03-09', refrigerantCareLevel: 98 },
                      { date: '2025-03-10', refrigerantCareLevel: 98 },
                      { date: '2025-03-11', refrigerantCareLevel: 98 },
                      { date: '2025-03-12', refrigerantCareLevel: 97 },
                      { date: '2025-03-13', refrigerantCareLevel: 96 },
                      { date: '2025-03-14', refrigerantCareLevel: 95 },
                      { date: '2025-03-15', refrigerantCareLevel: 95 },
                      { date: '2025-03-16', refrigerantCareLevel: 94 },
                      { date: '2025-03-17', refrigerantCareLevel: 93 },
                      { date: '2025-03-18', refrigerantCareLevel: 93 },
                      { date: '2025-03-19', refrigerantCareLevel: 93 },
                      { date: '2025-03-20', refrigerantCareLevel: 93 },
                      { date: '2025-03-21', refrigerantCareLevel: 92 },
                      { date: '2025-03-22', refrigerantCareLevel: 91 },
                      { date: '2025-03-23', refrigerantCareLevel: 91 },
                      { date: '2025-03-24', refrigerantCareLevel: 91 },
                      { date: '2025-03-25', refrigerantCareLevel: 91 },
                      { date: '2025-03-26', refrigerantCareLevel: 91 },
                      { date: '2025-03-27', refrigerantCareLevel: 90 },
                      { date: '2025-03-28', refrigerantCareLevel: 90 },
                      { date: '2025-03-29', refrigerantCareLevel: 90 },
                      { date: '2025-03-30', refrigerantCareLevel: 89 },
                    ],
                  },
                  {
                    equipmentId: '35a44a55-546f-11ef-a42c-3d5adfbd80a0',
                    equipmentName: 'FXYFP28DA_1-01@6',
                    onOff: true,
                    currentRefrigerantCareLevel: 91,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 104 },
                      { date: '2025-03-02', refrigerantCareLevel: 103 },
                      { date: '2025-03-03', refrigerantCareLevel: 103 },
                      { date: '2025-03-04', refrigerantCareLevel: 103 },
                      { date: '2025-03-05', refrigerantCareLevel: 102 },
                      { date: '2025-03-06', refrigerantCareLevel: 102 },
                      { date: '2025-03-07', refrigerantCareLevel: 102 },
                      { date: '2025-03-08', refrigerantCareLevel: 101 },
                      { date: '2025-03-09', refrigerantCareLevel: 101 },
                      { date: '2025-03-10', refrigerantCareLevel: 100 },
                      { date: '2025-03-11', refrigerantCareLevel: 100 },
                      { date: '2025-03-12', refrigerantCareLevel: 99 },
                      { date: '2025-03-13', refrigerantCareLevel: 99 },
                      { date: '2025-03-14', refrigerantCareLevel: 99 },
                      { date: '2025-03-15', refrigerantCareLevel: 98 },
                      { date: '2025-03-16', refrigerantCareLevel: 98 },
                      { date: '2025-03-17', refrigerantCareLevel: 97 },
                      { date: '2025-03-18', refrigerantCareLevel: 96 },
                      { date: '2025-03-19', refrigerantCareLevel: 95 },
                      { date: '2025-03-20', refrigerantCareLevel: 95 },
                      { date: '2025-03-21', refrigerantCareLevel: 94 },
                      { date: '2025-03-22', refrigerantCareLevel: 94 },
                      { date: '2025-03-23', refrigerantCareLevel: 94 },
                      { date: '2025-03-24', refrigerantCareLevel: 94 },
                      { date: '2025-03-25', refrigerantCareLevel: 93 },
                      { date: '2025-03-26', refrigerantCareLevel: 93 },
                      { date: '2025-03-27', refrigerantCareLevel: 92 },
                      { date: '2025-03-28', refrigerantCareLevel: 92 },
                      { date: '2025-03-29', refrigerantCareLevel: 91 },
                      { date: '2025-03-30', refrigerantCareLevel: 91 },
                    ],
                  },
                ],
              },
              {
                zoneId: '225876ad-3aa9-11ef-8735-13398b5a0231',
                zoneName: 'EDGE-TDPK-002',
                equipmentList: [
                  {
                    equipmentId: 'cb19afb0-8777-11ef-b492-1f7e694e3ee0',
                    equipmentName: 'FXYFP28DA_1-01@7',
                    onOff: true,
                    currentRefrigerantCareLevel: 92,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 104 },
                      { date: '2025-03-02', refrigerantCareLevel: 104 },
                      { date: '2025-03-03', refrigerantCareLevel: 104 },
                      { date: '2025-03-04', refrigerantCareLevel: 103 },
                      { date: '2025-03-05', refrigerantCareLevel: 103 },
                      { date: '2025-03-06', refrigerantCareLevel: 103 },
                      { date: '2025-03-07', refrigerantCareLevel: 102 },
                      { date: '2025-03-08', refrigerantCareLevel: 102 },
                      { date: '2025-03-09', refrigerantCareLevel: 102 },
                      { date: '2025-03-10', refrigerantCareLevel: 101 },
                      { date: '2025-03-11', refrigerantCareLevel: 100 },
                      { date: '2025-03-12', refrigerantCareLevel: 100 },
                      { date: '2025-03-13', refrigerantCareLevel: 100 },
                      { date: '2025-03-14', refrigerantCareLevel: 99 },
                      { date: '2025-03-15', refrigerantCareLevel: 99 },
                      { date: '2025-03-16', refrigerantCareLevel: 98 },
                      { date: '2025-03-17', refrigerantCareLevel: 98 },
                      { date: '2025-03-18', refrigerantCareLevel: 98 },
                      { date: '2025-03-19', refrigerantCareLevel: 98 },
                      { date: '2025-03-20', refrigerantCareLevel: 96 },
                      { date: '2025-03-21', refrigerantCareLevel: 96 },
                      { date: '2025-03-22', refrigerantCareLevel: 96 },
                      { date: '2025-03-23', refrigerantCareLevel: 95 },
                      { date: '2025-03-24', refrigerantCareLevel: 95 },
                      { date: '2025-03-25', refrigerantCareLevel: 95 },
                      { date: '2025-03-26', refrigerantCareLevel: 94 },
                      { date: '2025-03-27', refrigerantCareLevel: 94 },
                      { date: '2025-03-28', refrigerantCareLevel: 94 },
                      { date: '2025-03-29', refrigerantCareLevel: 94 },
                      { date: '2025-03-30', refrigerantCareLevel: 92 },
                    ],
                  },
                  {
                    equipmentId: 'ffc52900-d876-11ef-94d4-4d22882cea9f',
                    equipmentName: 'FXYFP28DA_1-01@1',
                    onOff: false,
                    currentRefrigerantCareLevel: 93,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 105 },
                      { date: '2025-03-02', refrigerantCareLevel: 105 },
                      { date: '2025-03-03', refrigerantCareLevel: 105 },
                      { date: '2025-03-04', refrigerantCareLevel: 104 },
                      { date: '2025-03-05', refrigerantCareLevel: 104 },
                      { date: '2025-03-06', refrigerantCareLevel: 103 },
                      { date: '2025-03-07', refrigerantCareLevel: 103 },
                      { date: '2025-03-08', refrigerantCareLevel: 100 },
                      { date: '2025-03-09', refrigerantCareLevel: 99 },
                      { date: '2025-03-10', refrigerantCareLevel: 99 },
                      { date: '2025-03-11', refrigerantCareLevel: 99 },
                      { date: '2025-03-12', refrigerantCareLevel: 98 },
                      { date: '2025-03-13', refrigerantCareLevel: 98 },
                      { date: '2025-03-14', refrigerantCareLevel: 98 },
                      { date: '2025-03-15', refrigerantCareLevel: 97 },
                      { date: '2025-03-16', refrigerantCareLevel: 97 },
                      { date: '2025-03-17', refrigerantCareLevel: 97 },
                      { date: '2025-03-18', refrigerantCareLevel: 97 },
                      { date: '2025-03-19', refrigerantCareLevel: 97 },
                      { date: '2025-03-20', refrigerantCareLevel: 96 },
                      { date: '2025-03-21', refrigerantCareLevel: 96 },
                      { date: '2025-03-22', refrigerantCareLevel: 96 },
                      { date: '2025-03-23', refrigerantCareLevel: 96 },
                      { date: '2025-03-24', refrigerantCareLevel: 95 },
                      { date: '2025-03-25', refrigerantCareLevel: 94 },
                      { date: '2025-03-26', refrigerantCareLevel: 94 },
                      { date: '2025-03-27', refrigerantCareLevel: 94 },
                      { date: '2025-03-28', refrigerantCareLevel: 93 },
                      { date: '2025-03-29', refrigerantCareLevel: 93 },
                      { date: '2025-03-30', refrigerantCareLevel: 93 },
                    ],
                  },
                ],
              },
              {
                zoneId: 'da2f4cd6-74ee-11ef-8e1f-cf69102e343e',
                zoneName: 'TDPK_Zone1',
                equipmentList: [
                  {
                    equipmentId: '35a6d8c0-546f-11ef-b212-3d5adfbd80a0',
                    equipmentName: 'FXYFP28DA_1-01@2',
                    onOff: true,
                    currentRefrigerantCareLevel: 95,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 105 },
                      { date: '2025-03-02', refrigerantCareLevel: 105 },
                      { date: '2025-03-03', refrigerantCareLevel: 105 },
                      { date: '2025-03-04', refrigerantCareLevel: 104 },
                      { date: '2025-03-05', refrigerantCareLevel: 104 },
                      { date: '2025-03-06', refrigerantCareLevel: 104 },
                      { date: '2025-03-07', refrigerantCareLevel: 103 },
                      { date: '2025-03-08', refrigerantCareLevel: 102 },
                      { date: '2025-03-09', refrigerantCareLevel: 102 },
                      { date: '2025-03-10', refrigerantCareLevel: 100 },
                      { date: '2025-03-11', refrigerantCareLevel: 100 },
                      { date: '2025-03-12', refrigerantCareLevel: 99 },
                      { date: '2025-03-13', refrigerantCareLevel: 99 },
                      { date: '2025-03-14', refrigerantCareLevel: 99 },
                      { date: '2025-03-15', refrigerantCareLevel: 99 },
                      { date: '2025-03-16', refrigerantCareLevel: 98 },
                      { date: '2025-03-17', refrigerantCareLevel: 97 },
                      { date: '2025-03-18', refrigerantCareLevel: 97 },
                      { date: '2025-03-19', refrigerantCareLevel: 97 },
                      { date: '2025-03-20', refrigerantCareLevel: 97 },
                      { date: '2025-03-21', refrigerantCareLevel: 97 },
                      { date: '2025-03-22', refrigerantCareLevel: 97 },
                      { date: '2025-03-23', refrigerantCareLevel: 96 },
                      { date: '2025-03-24', refrigerantCareLevel: 96 },
                      { date: '2025-03-25', refrigerantCareLevel: 96 },
                      { date: '2025-03-26', refrigerantCareLevel: 95 },
                      { date: '2025-03-27', refrigerantCareLevel: 95 },
                      { date: '2025-03-28', refrigerantCareLevel: 95 },
                      { date: '2025-03-29', refrigerantCareLevel: 95 },
                      { date: '2025-03-30', refrigerantCareLevel: 95 },
                    ],
                  },
                  {
                    equipmentId: '35a4e047-546f-11ef-8408-3d5adfbd80a0',
                    equipmentName: 'FXYFP28DA_1-01@3',
                    onOff: true,
                    currentRefrigerantCareLevel: 96,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 108 },
                      { date: '2025-03-02', refrigerantCareLevel: 108 },
                      { date: '2025-03-03', refrigerantCareLevel: 106 },
                      { date: '2025-03-04', refrigerantCareLevel: 106 },
                      { date: '2025-03-05', refrigerantCareLevel: 106 },
                      { date: '2025-03-06', refrigerantCareLevel: 106 },
                      { date: '2025-03-07', refrigerantCareLevel: 106 },
                      { date: '2025-03-08', refrigerantCareLevel: 106 },
                      { date: '2025-03-09', refrigerantCareLevel: 105 },
                      { date: '2025-03-10', refrigerantCareLevel: 105 },
                      { date: '2025-03-11', refrigerantCareLevel: 104 },
                      { date: '2025-03-12', refrigerantCareLevel: 103 },
                      { date: '2025-03-13', refrigerantCareLevel: 102 },
                      { date: '2025-03-14', refrigerantCareLevel: 102 },
                      { date: '2025-03-15', refrigerantCareLevel: 102 },
                      { date: '2025-03-16', refrigerantCareLevel: 102 },
                      { date: '2025-03-17', refrigerantCareLevel: 101 },
                      { date: '2025-03-18', refrigerantCareLevel: 101 },
                      { date: '2025-03-19', refrigerantCareLevel: 101 },
                      { date: '2025-03-20', refrigerantCareLevel: 100 },
                      { date: '2025-03-21', refrigerantCareLevel: 100 },
                      { date: '2025-03-22', refrigerantCareLevel: 100 },
                      { date: '2025-03-23', refrigerantCareLevel: 99 },
                      { date: '2025-03-24', refrigerantCareLevel: 98 },
                      { date: '2025-03-25', refrigerantCareLevel: 98 },
                      { date: '2025-03-26', refrigerantCareLevel: 98 },
                      { date: '2025-03-27', refrigerantCareLevel: 98 },
                      { date: '2025-03-28', refrigerantCareLevel: 97 },
                      { date: '2025-03-29', refrigerantCareLevel: 97 },
                      { date: '2025-03-30', refrigerantCareLevel: 96 },
                    ],
                  },
                ],
              },
              {
                zoneId: '2381c5f6-3aa9-11ef-9831-13398b5a0231',
                zoneName: 'EDGE-TDPK-002_outdoor',
                equipmentList: [
                  {
                    equipmentId: '35a5a68a-546f-11ef-8a16-3d5adfbd80a0',
                    equipmentName: 'REYP1120F_m3_an1@1',
                    onOff: true,
                    currentRefrigerantCareLevel: 98,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 110 },
                      { date: '2025-03-02', refrigerantCareLevel: 109 },
                      { date: '2025-03-03', refrigerantCareLevel: 109 },
                      { date: '2025-03-04', refrigerantCareLevel: 108 },
                      { date: '2025-03-05', refrigerantCareLevel: 108 },
                      { date: '2025-03-06', refrigerantCareLevel: 107 },
                      { date: '2025-03-07', refrigerantCareLevel: 107 },
                      { date: '2025-03-08', refrigerantCareLevel: 107 },
                      { date: '2025-03-09', refrigerantCareLevel: 106 },
                      { date: '2025-03-10', refrigerantCareLevel: 106 },
                      { date: '2025-03-11', refrigerantCareLevel: 106 },
                      { date: '2025-03-12', refrigerantCareLevel: 106 },
                      { date: '2025-03-13', refrigerantCareLevel: 105 },
                      { date: '2025-03-14', refrigerantCareLevel: 105 },
                      { date: '2025-03-15', refrigerantCareLevel: 105 },
                      { date: '2025-03-16', refrigerantCareLevel: 105 },
                      { date: '2025-03-17', refrigerantCareLevel: 104 },
                      { date: '2025-03-18', refrigerantCareLevel: 104 },
                      { date: '2025-03-19', refrigerantCareLevel: 104 },
                      { date: '2025-03-20', refrigerantCareLevel: 103 },
                      { date: '2025-03-21', refrigerantCareLevel: 103 },
                      { date: '2025-03-22', refrigerantCareLevel: 103 },
                      { date: '2025-03-23', refrigerantCareLevel: 102 },
                      { date: '2025-03-24', refrigerantCareLevel: 102 },
                      { date: '2025-03-25', refrigerantCareLevel: 101 },
                      { date: '2025-03-26', refrigerantCareLevel: 101 },
                      { date: '2025-03-27', refrigerantCareLevel: 100 },
                      { date: '2025-03-28', refrigerantCareLevel: 99 },
                      { date: '2025-03-29', refrigerantCareLevel: 99 },
                      { date: '2025-03-30', refrigerantCareLevel: 99 },
                    ],
                  },
                ],
              },
            ],
          },
          {
            edgeId: 'c6e86053-86cd-11ef-92ea-39ee8303dd96',
            edgeName: 'EDGE-TDPK-ADD',
            edgeStates: {
              errors: true,
              forcedStop: false,
              maintenance: false,
              normal: true,
            },
            zoneList: [
              {
                zoneId: '270afe30-86cf-11ef-a044-a3d0fec74d6d',
                zoneName: 'EDGE-TDPK-ADD',
                equipmentList: [
                  {
                    equipmentId: 'd3edc029-86ce-11ef-9147-37993a066d00',
                    equipmentName: 'Meter1',
                    onOff: true,
                    currentRefrigerantCareLevel: 99,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 110 },
                      { date: '2025-03-02', refrigerantCareLevel: 110 },
                      { date: '2025-03-03', refrigerantCareLevel: 110 },
                      { date: '2025-03-04', refrigerantCareLevel: 109 },
                      { date: '2025-03-05', refrigerantCareLevel: 109 },
                      { date: '2025-03-06', refrigerantCareLevel: 108 },
                      { date: '2025-03-07', refrigerantCareLevel: 108 },
                      { date: '2025-03-08', refrigerantCareLevel: 108 },
                      { date: '2025-03-09', refrigerantCareLevel: 107 },
                      { date: '2025-03-10', refrigerantCareLevel: 107 },
                      { date: '2025-03-11', refrigerantCareLevel: 106 },
                      { date: '2025-03-12', refrigerantCareLevel: 106 },
                      { date: '2025-03-13', refrigerantCareLevel: 106 },
                      { date: '2025-03-14', refrigerantCareLevel: 106 },
                      { date: '2025-03-15', refrigerantCareLevel: 105 },
                      { date: '2025-03-16', refrigerantCareLevel: 104 },
                      { date: '2025-03-17', refrigerantCareLevel: 104 },
                      { date: '2025-03-18', refrigerantCareLevel: 104 },
                      { date: '2025-03-19', refrigerantCareLevel: 103 },
                      { date: '2025-03-20', refrigerantCareLevel: 103 },
                      { date: '2025-03-21', refrigerantCareLevel: 101 },
                      { date: '2025-03-22', refrigerantCareLevel: 101 },
                      { date: '2025-03-23', refrigerantCareLevel: 101 },
                      { date: '2025-03-24', refrigerantCareLevel: 101 },
                      { date: '2025-03-25', refrigerantCareLevel: 100 },
                      { date: '2025-03-26', refrigerantCareLevel: 100 },
                      { date: '2025-03-27', refrigerantCareLevel: 100 },
                      { date: '2025-03-28', refrigerantCareLevel: 100 },
                      { date: '2025-03-29', refrigerantCareLevel: 100 },
                      { date: '2025-03-30', refrigerantCareLevel: 99 },
                    ],
                  },
                  {
                    equipmentId: '03ad31b8-95c1-11ef-bb6c-a33251b84dee',
                    equipmentName: 'TDPK-ID-4',
                    onOff: true,
                    currentRefrigerantCareLevel: 100,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 110 },
                      { date: '2025-03-02', refrigerantCareLevel: 109 },
                      { date: '2025-03-03', refrigerantCareLevel: 109 },
                      { date: '2025-03-04', refrigerantCareLevel: 109 },
                      { date: '2025-03-05', refrigerantCareLevel: 109 },
                      { date: '2025-03-06', refrigerantCareLevel: 108 },
                      { date: '2025-03-07', refrigerantCareLevel: 108 },
                      { date: '2025-03-08', refrigerantCareLevel: 108 },
                      { date: '2025-03-09', refrigerantCareLevel: 107 },
                      { date: '2025-03-10', refrigerantCareLevel: 106 },
                      { date: '2025-03-11', refrigerantCareLevel: 106 },
                      { date: '2025-03-12', refrigerantCareLevel: 106 },
                      { date: '2025-03-13', refrigerantCareLevel: 106 },
                      { date: '2025-03-14', refrigerantCareLevel: 105 },
                      { date: '2025-03-15', refrigerantCareLevel: 105 },
                      { date: '2025-03-16', refrigerantCareLevel: 104 },
                      { date: '2025-03-17', refrigerantCareLevel: 103 },
                      { date: '2025-03-18', refrigerantCareLevel: 103 },
                      { date: '2025-03-19', refrigerantCareLevel: 103 },
                      { date: '2025-03-20', refrigerantCareLevel: 103 },
                      { date: '2025-03-21', refrigerantCareLevel: 103 },
                      { date: '2025-03-22', refrigerantCareLevel: 103 },
                      { date: '2025-03-23', refrigerantCareLevel: 102 },
                      { date: '2025-03-24', refrigerantCareLevel: 102 },
                      { date: '2025-03-25', refrigerantCareLevel: 102 },
                      { date: '2025-03-26', refrigerantCareLevel: 102 },
                      { date: '2025-03-27', refrigerantCareLevel: 100 },
                      { date: '2025-03-28', refrigerantCareLevel: 100 },
                      { date: '2025-03-29', refrigerantCareLevel: 100 },
                      { date: '2025-03-30', refrigerantCareLevel: 100 },
                    ],
                  },
                  {
                    equipmentId: '03ac6463-95c1-11ef-a31b-a33251b84dee',
                    equipmentName: 'TDPK-ID-7',
                    onOff: true,
                    currentRefrigerantCareLevel: 101,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 111 },
                      { date: '2025-03-02', refrigerantCareLevel: 110 },
                      { date: '2025-03-03', refrigerantCareLevel: 110 },
                      { date: '2025-03-04', refrigerantCareLevel: 109 },
                      { date: '2025-03-05', refrigerantCareLevel: 109 },
                      { date: '2025-03-06', refrigerantCareLevel: 109 },
                      { date: '2025-03-07', refrigerantCareLevel: 109 },
                      { date: '2025-03-08', refrigerantCareLevel: 109 },
                      { date: '2025-03-09', refrigerantCareLevel: 108 },
                      { date: '2025-03-10', refrigerantCareLevel: 108 },
                      { date: '2025-03-11', refrigerantCareLevel: 107 },
                      { date: '2025-03-12', refrigerantCareLevel: 107 },
                      { date: '2025-03-13', refrigerantCareLevel: 107 },
                      { date: '2025-03-14', refrigerantCareLevel: 106 },
                      { date: '2025-03-15', refrigerantCareLevel: 106 },
                      { date: '2025-03-16', refrigerantCareLevel: 106 },
                      { date: '2025-03-17', refrigerantCareLevel: 106 },
                      { date: '2025-03-18', refrigerantCareLevel: 106 },
                      { date: '2025-03-19', refrigerantCareLevel: 105 },
                      { date: '2025-03-20', refrigerantCareLevel: 105 },
                      { date: '2025-03-21', refrigerantCareLevel: 104 },
                      { date: '2025-03-22', refrigerantCareLevel: 104 },
                      { date: '2025-03-23', refrigerantCareLevel: 104 },
                      { date: '2025-03-24', refrigerantCareLevel: 104 },
                      { date: '2025-03-25', refrigerantCareLevel: 104 },
                      { date: '2025-03-26', refrigerantCareLevel: 104 },
                      { date: '2025-03-27', refrigerantCareLevel: 102 },
                      { date: '2025-03-28', refrigerantCareLevel: 102 },
                      { date: '2025-03-29', refrigerantCareLevel: 102 },
                      { date: '2025-03-30', refrigerantCareLevel: 101 },
                    ],
                  },
                  {
                    equipmentId: '03af8b2f-95c1-11ef-8f7a-a33251b84dee',
                    equipmentName: 'TDPK-ID-2',
                    onOff: true,
                    currentRefrigerantCareLevel: 102,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 112 },
                      { date: '2025-03-02', refrigerantCareLevel: 111 },
                      { date: '2025-03-03', refrigerantCareLevel: 111 },
                      { date: '2025-03-04', refrigerantCareLevel: 110 },
                      { date: '2025-03-05', refrigerantCareLevel: 110 },
                      { date: '2025-03-06', refrigerantCareLevel: 109 },
                      { date: '2025-03-07', refrigerantCareLevel: 109 },
                      { date: '2025-03-08', refrigerantCareLevel: 109 },
                      { date: '2025-03-09', refrigerantCareLevel: 109 },
                      { date: '2025-03-10', refrigerantCareLevel: 108 },
                      { date: '2025-03-11', refrigerantCareLevel: 108 },
                      { date: '2025-03-12', refrigerantCareLevel: 108 },
                      { date: '2025-03-13', refrigerantCareLevel: 107 },
                      { date: '2025-03-14', refrigerantCareLevel: 107 },
                      { date: '2025-03-15', refrigerantCareLevel: 107 },
                      { date: '2025-03-16', refrigerantCareLevel: 107 },
                      { date: '2025-03-17', refrigerantCareLevel: 106 },
                      { date: '2025-03-18', refrigerantCareLevel: 105 },
                      { date: '2025-03-19', refrigerantCareLevel: 105 },
                      { date: '2025-03-20', refrigerantCareLevel: 105 },
                      { date: '2025-03-21', refrigerantCareLevel: 104 },
                      { date: '2025-03-22', refrigerantCareLevel: 104 },
                      { date: '2025-03-23', refrigerantCareLevel: 104 },
                      { date: '2025-03-24', refrigerantCareLevel: 103 },
                      { date: '2025-03-25', refrigerantCareLevel: 103 },
                      { date: '2025-03-26', refrigerantCareLevel: 103 },
                      { date: '2025-03-27', refrigerantCareLevel: 103 },
                      { date: '2025-03-28', refrigerantCareLevel: 103 },
                      { date: '2025-03-29', refrigerantCareLevel: 102 },
                      { date: '2025-03-30', refrigerantCareLevel: 102 },
                    ],
                  },
                  {
                    equipmentId: '03afea9d-95c1-11ef-9d83-a33251b84dee',
                    equipmentName: 'TDPK-ID-5',
                    onOff: true,
                    currentRefrigerantCareLevel: 104,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 113 },
                      { date: '2025-03-02', refrigerantCareLevel: 113 },
                      { date: '2025-03-03', refrigerantCareLevel: 113 },
                      { date: '2025-03-04', refrigerantCareLevel: 112 },
                      { date: '2025-03-05', refrigerantCareLevel: 112 },
                      { date: '2025-03-06', refrigerantCareLevel: 112 },
                      { date: '2025-03-07', refrigerantCareLevel: 112 },
                      { date: '2025-03-08', refrigerantCareLevel: 112 },
                      { date: '2025-03-09', refrigerantCareLevel: 112 },
                      { date: '2025-03-10', refrigerantCareLevel: 112 },
                      { date: '2025-03-11', refrigerantCareLevel: 112 },
                      { date: '2025-03-12', refrigerantCareLevel: 111 },
                      { date: '2025-03-13', refrigerantCareLevel: 111 },
                      { date: '2025-03-14', refrigerantCareLevel: 111 },
                      { date: '2025-03-15', refrigerantCareLevel: 111 },
                      { date: '2025-03-16', refrigerantCareLevel: 110 },
                      { date: '2025-03-17', refrigerantCareLevel: 110 },
                      { date: '2025-03-18', refrigerantCareLevel: 110 },
                      { date: '2025-03-19', refrigerantCareLevel: 109 },
                      { date: '2025-03-20', refrigerantCareLevel: 109 },
                      { date: '2025-03-21', refrigerantCareLevel: 109 },
                      { date: '2025-03-22', refrigerantCareLevel: 109 },
                      { date: '2025-03-23', refrigerantCareLevel: 109 },
                      { date: '2025-03-24', refrigerantCareLevel: 108 },
                      { date: '2025-03-25', refrigerantCareLevel: 107 },
                      { date: '2025-03-26', refrigerantCareLevel: 107 },
                      { date: '2025-03-27', refrigerantCareLevel: 107 },
                      { date: '2025-03-28', refrigerantCareLevel: 106 },
                      { date: '2025-03-29', refrigerantCareLevel: 105 },
                      { date: '2025-03-30', refrigerantCareLevel: 104 },
                    ],
                  },
                  {
                    equipmentId: '03ade72d-95c1-11ef-82a9-a33251b84dee',
                    equipmentName: 'TDPK-ID-1',
                    onOff: true,
                    currentRefrigerantCareLevel: 106,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 115 },
                      { date: '2025-03-02', refrigerantCareLevel: 115 },
                      { date: '2025-03-03', refrigerantCareLevel: 114 },
                      { date: '2025-03-04', refrigerantCareLevel: 114 },
                      { date: '2025-03-05', refrigerantCareLevel: 113 },
                      { date: '2025-03-06', refrigerantCareLevel: 113 },
                      { date: '2025-03-07', refrigerantCareLevel: 113 },
                      { date: '2025-03-08', refrigerantCareLevel: 113 },
                      { date: '2025-03-09', refrigerantCareLevel: 112 },
                      { date: '2025-03-10', refrigerantCareLevel: 112 },
                      { date: '2025-03-11', refrigerantCareLevel: 112 },
                      { date: '2025-03-12', refrigerantCareLevel: 111 },
                      { date: '2025-03-13', refrigerantCareLevel: 111 },
                      { date: '2025-03-14', refrigerantCareLevel: 111 },
                      { date: '2025-03-15', refrigerantCareLevel: 111 },
                      { date: '2025-03-16', refrigerantCareLevel: 110 },
                      { date: '2025-03-17', refrigerantCareLevel: 110 },
                      { date: '2025-03-18', refrigerantCareLevel: 110 },
                      { date: '2025-03-19', refrigerantCareLevel: 110 },
                      { date: '2025-03-20', refrigerantCareLevel: 110 },
                      { date: '2025-03-21', refrigerantCareLevel: 110 },
                      { date: '2025-03-22', refrigerantCareLevel: 109 },
                      { date: '2025-03-23', refrigerantCareLevel: 109 },
                      { date: '2025-03-24', refrigerantCareLevel: 109 },
                      { date: '2025-03-25', refrigerantCareLevel: 109 },
                      { date: '2025-03-26', refrigerantCareLevel: 109 },
                      { date: '2025-03-27', refrigerantCareLevel: 108 },
                      { date: '2025-03-28', refrigerantCareLevel: 108 },
                      { date: '2025-03-29', refrigerantCareLevel: 108 },
                      { date: '2025-03-30', refrigerantCareLevel: 107 },
                    ],
                  },
                  {
                    equipmentId: '03acd60c-95c1-11ef-bd04-a33251b84dee',
                    equipmentName: 'TDPK-ID-6',
                    onOff: true,
                    currentRefrigerantCareLevel: 107,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 118 },
                      { date: '2025-03-02', refrigerantCareLevel: 117 },
                      { date: '2025-03-03', refrigerantCareLevel: 117 },
                      { date: '2025-03-04', refrigerantCareLevel: 117 },
                      { date: '2025-03-05', refrigerantCareLevel: 116 },
                      { date: '2025-03-06', refrigerantCareLevel: 116 },
                      { date: '2025-03-07', refrigerantCareLevel: 116 },
                      { date: '2025-03-08', refrigerantCareLevel: 116 },
                      { date: '2025-03-09', refrigerantCareLevel: 116 },
                      { date: '2025-03-10', refrigerantCareLevel: 115 },
                      { date: '2025-03-11', refrigerantCareLevel: 115 },
                      { date: '2025-03-12', refrigerantCareLevel: 115 },
                      { date: '2025-03-13', refrigerantCareLevel: 115 },
                      { date: '2025-03-14', refrigerantCareLevel: 113 },
                      { date: '2025-03-15', refrigerantCareLevel: 113 },
                      { date: '2025-03-16', refrigerantCareLevel: 113 },
                      { date: '2025-03-17', refrigerantCareLevel: 113 },
                      { date: '2025-03-18', refrigerantCareLevel: 112 },
                      { date: '2025-03-19', refrigerantCareLevel: 112 },
                      { date: '2025-03-20', refrigerantCareLevel: 112 },
                      { date: '2025-03-21', refrigerantCareLevel: 112 },
                      { date: '2025-03-22', refrigerantCareLevel: 111 },
                      { date: '2025-03-23', refrigerantCareLevel: 111 },
                      { date: '2025-03-24', refrigerantCareLevel: 111 },
                      { date: '2025-03-25', refrigerantCareLevel: 111 },
                      { date: '2025-03-26', refrigerantCareLevel: 110 },
                      { date: '2025-03-27', refrigerantCareLevel: 109 },
                      { date: '2025-03-28', refrigerantCareLevel: 109 },
                      { date: '2025-03-29', refrigerantCareLevel: 108 },
                      { date: '2025-03-30', refrigerantCareLevel: 108 },
                    ],
                  },
                  {
                    equipmentId: '03ad8d42-95c1-11ef-a54c-a33251b84dee',
                    equipmentName: 'TDPK-ID-3',
                    onOff: true,
                    currentRefrigerantCareLevel: null,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 'null' },
                      { date: '2025-03-02', refrigerantCareLevel: 'null' },
                      { date: '2025-03-03', refrigerantCareLevel: 'null' },
                      { date: '2025-03-04', refrigerantCareLevel: 'null' },
                      { date: '2025-03-05', refrigerantCareLevel: 'null' },
                      { date: '2025-03-06', refrigerantCareLevel: 'null' },
                      { date: '2025-03-07', refrigerantCareLevel: 'null' },
                      { date: '2025-03-08', refrigerantCareLevel: 'null' },
                      { date: '2025-03-09', refrigerantCareLevel: 'null' },
                      { date: '2025-03-10', refrigerantCareLevel: 'null' },
                      { date: '2025-03-11', refrigerantCareLevel: 'null' },
                      { date: '2025-03-12', refrigerantCareLevel: 'null' },
                      { date: '2025-03-13', refrigerantCareLevel: 'null' },
                      { date: '2025-03-14', refrigerantCareLevel: 'null' },
                      { date: '2025-03-15', refrigerantCareLevel: 'null' },
                      { date: '2025-03-16', refrigerantCareLevel: 'null' },
                      { date: '2025-03-17', refrigerantCareLevel: 'null' },
                      { date: '2025-03-18', refrigerantCareLevel: 'null' },
                      { date: '2025-03-19', refrigerantCareLevel: 'null' },
                      { date: '2025-03-20', refrigerantCareLevel: 'null' },
                      { date: '2025-03-21', refrigerantCareLevel: 'null' },
                      { date: '2025-03-22', refrigerantCareLevel: 'null' },
                      { date: '2025-03-23', refrigerantCareLevel: 'null' },
                      { date: '2025-03-24', refrigerantCareLevel: 'null' },
                      { date: '2025-03-25', refrigerantCareLevel: 'null' },
                      { date: '2025-03-26', refrigerantCareLevel: 'null' },
                      { date: '2025-03-27', refrigerantCareLevel: 'null' },
                      { date: '2025-03-28', refrigerantCareLevel: 'null' },
                      { date: '2025-03-29', refrigerantCareLevel: 'null' },
                      { date: '2025-03-30', refrigerantCareLevel: 'null' },
                    ],
                  },
                ],
              },
              {
                zoneId: '28448378-86cf-11ef-8282-a3d0fec74d6d',
                zoneName: 'EDGE-TDPK-ADD_outdoor',
                equipmentList: [
                  {
                    equipmentId: '03abfbcc-95c1-11ef-abae-a33251b84dee',
                    equipmentName: 'TDPK-OD-1',
                    onOff: true,
                    currentRefrigerantCareLevel: null,
                    refrigerantCareLevelList: [
                      { date: '2025-03-01', refrigerantCareLevel: 'null' },
                      { date: '2025-03-02', refrigerantCareLevel: 'null' },
                      { date: '2025-03-03', refrigerantCareLevel: 'null' },
                      { date: '2025-03-04', refrigerantCareLevel: 'null' },
                      { date: '2025-03-05', refrigerantCareLevel: 'null' },
                      { date: '2025-03-06', refrigerantCareLevel: 'null' },
                      { date: '2025-03-07', refrigerantCareLevel: 'null' },
                      { date: '2025-03-08', refrigerantCareLevel: 'null' },
                      { date: '2025-03-09', refrigerantCareLevel: 'null' },
                      { date: '2025-03-10', refrigerantCareLevel: 'null' },
                      { date: '2025-03-11', refrigerantCareLevel: 'null' },
                      { date: '2025-03-12', refrigerantCareLevel: 'null' },
                      { date: '2025-03-13', refrigerantCareLevel: 'null' },
                      { date: '2025-03-14', refrigerantCareLevel: 'null' },
                      { date: '2025-03-15', refrigerantCareLevel: 'null' },
                      { date: '2025-03-16', refrigerantCareLevel: 'null' },
                      { date: '2025-03-17', refrigerantCareLevel: 'null' },
                      { date: '2025-03-18', refrigerantCareLevel: 'null' },
                      { date: '2025-03-19', refrigerantCareLevel: 'null' },
                      { date: '2025-03-20', refrigerantCareLevel: 'null' },
                      { date: '2025-03-21', refrigerantCareLevel: 'null' },
                      { date: '2025-03-22', refrigerantCareLevel: 'null' },
                      { date: '2025-03-23', refrigerantCareLevel: 'null' },
                      { date: '2025-03-24', refrigerantCareLevel: 'null' },
                      { date: '2025-03-25', refrigerantCareLevel: 'null' },
                      { date: '2025-03-26', refrigerantCareLevel: 'null' },
                      { date: '2025-03-27', refrigerantCareLevel: 'null' },
                      { date: '2025-03-28', refrigerantCareLevel: 'null' },
                      { date: '2025-03-29', refrigerantCareLevel: 'null' },
                      { date: '2025-03-30', refrigerantCareLevel: 'null' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
